import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { mapCreateFormToOfflineUpsertRequest } from 'src/services/manage_suppliers/mapOfflineUpsertRequest';
import useSendOfflineSupplierEmailVerificationMutation from 'src/services/manage_suppliers/useSendOfflineSupplierEmailVerificationMutation';
import useUpsertOfflineSupplierMutation from 'src/services/manage_suppliers/useUpsertOfflineSupplierMutation';
import { validateOfflineApId } from 'src/services/manage_suppliers/useValidateOfflineApIdMutation';
import { validateOfflineSupplierName } from 'src/services/manage_suppliers/useValidateOfflineSupplierNameMutation';
import {
  CreateOfflineSupplierFormValues,
  initialCreateOfflineSupplierFormValues,
} from 'src/types/manage-suppliers/CreateOfflineSupplierForm';
import { SupplierTransmissionType } from 'src/types/manage-suppliers/SupplierForm';
import useSupplierRequiredFields from './useSupplierRequiredFields';
import {
  CREATE_SUPPLIER_FIELD_ACCESS,
  fromCreateFormValues,
  getSupplierFormSyncErrors,
} from './utils/supplierFormValues';

type FormField = keyof CreateOfflineSupplierFormValues;
type AsyncField = 'supplierName' | 'apId';

interface UseCreateOfflineSupplierFormOptions {
  isOpen: boolean;
  onSuccess?: () => void;
}

export const useCreateOfflineSupplierForm = ({
  isOpen,
  onSuccess,
}: UseCreateOfflineSupplierFormOptions) => {
  const { t } = useTranslation();
  const requiredFields = useSupplierRequiredFields();
  const { mutateAsync: upsertOfflineSupplier, isPending: isSaving } =
    useUpsertOfflineSupplierMutation();
  const { mutateAsync: sendEmailVerification } = useSendOfflineSupplierEmailVerificationMutation();
  const [values, setValues] = useState<CreateOfflineSupplierFormValues>(
    initialCreateOfflineSupplierFormValues
  );
  const [touched, setTouched] = useState<Partial<Record<FormField, boolean>>>({});
  const [asyncErrors, setAsyncErrors] = useState<Partial<Record<AsyncField, string>>>({});
  const validationCacheRef = useRef<
    Partial<Record<AsyncField, { value: string; isDuplicate: boolean }>>
  >({});
  const inflightValidationRef = useRef<
    Partial<Record<AsyncField, { value: string; promise: Promise<boolean> }>>
  >({});

  const resetForm = useCallback(() => {
    setValues(initialCreateOfflineSupplierFormValues);
    setTouched({});
    setAsyncErrors({});
    validationCacheRef.current = {};
    inflightValidationRef.current = {};
  }, []);

  useEffect(() => {
    if (isOpen) {
      resetForm();
    }
  }, [isOpen, resetForm]);

  const setFieldValue = useCallback(
    (field: FormField, value: string | SupplierTransmissionType) => {
      setValues((prev) => {
        if (field === 'supplierName' || field === 'apId') {
          delete validationCacheRef.current[field];
          delete inflightValidationRef.current[field];
        }

        if (field === 'externalSupplierName') {
          delete validationCacheRef.current.apId;
          delete inflightValidationRef.current.apId;
        }
        return { ...prev, [field]: value };
      });
    },
    []
  );

  const applyDuplicateResult = useCallback(
    (field: AsyncField, value: string, isDuplicate: boolean) => {
      validationCacheRef.current[field] = { value, isDuplicate };
      setAsyncErrors((prev) => {
        const next = { ...prev };
        if (isDuplicate) {
          next[field] =
            field === 'supplierName'
              ? t('This name already exists. Please enter another.')
              : t('This ID already exists. Please enter another.');
        } else {
          delete next[field];
        }
        return next;
      });
      return !isDuplicate;
    },
    [t]
  );

  const checkSupplierName = useCallback(
    async (supplierName: string) => {
      const trimmed = supplierName.trim();
      if (!trimmed) {
        delete validationCacheRef.current.supplierName;
        delete inflightValidationRef.current.supplierName;
        setAsyncErrors((prev) => {
          const next = { ...prev };
          delete next.supplierName;
          return next;
        });
        return true;
      }

      const cached = validationCacheRef.current.supplierName;
      if (cached?.value === trimmed) {
        return applyDuplicateResult('supplierName', trimmed, cached.isDuplicate);
      }

      const inflight = inflightValidationRef.current.supplierName;
      if (inflight?.value === trimmed) {
        return inflight.promise;
      }

      const promise = (async () => {
        try {
          const isDuplicate = await validateOfflineSupplierName({ supplierName: trimmed });
          return applyDuplicateResult('supplierName', trimmed, isDuplicate);
        } finally {
          if (inflightValidationRef.current.supplierName?.value === trimmed) {
            delete inflightValidationRef.current.supplierName;
          }
        }
      })();

      inflightValidationRef.current.supplierName = { value: trimmed, promise };
      return promise;
    },
    [applyDuplicateResult]
  );

  const checkApId = useCallback(
    async (apId: string) => {
      const trimmed = apId.trim();
      if (!trimmed) {
        delete validationCacheRef.current.apId;
        delete inflightValidationRef.current.apId;
        setAsyncErrors((prev) => {
          const next = { ...prev };
          delete next.apId;
          return next;
        });
        return true;
      }

      const cached = validationCacheRef.current.apId;
      if (cached?.value === trimmed) {
        return applyDuplicateResult('apId', trimmed, cached.isDuplicate);
      }

      const inflight = inflightValidationRef.current.apId;
      if (inflight?.value === trimmed) {
        return inflight.promise;
      }

      const promise = (async () => {
        try {
          const isDuplicate = await validateOfflineApId({ apId: trimmed });
          return applyDuplicateResult('apId', trimmed, isDuplicate);
        } finally {
          if (inflightValidationRef.current.apId?.value === trimmed) {
            delete inflightValidationRef.current.apId;
          }
        }
      })();

      inflightValidationRef.current.apId = { value: trimmed, promise };
      return promise;
    },
    [applyDuplicateResult]
  );

  const handleFieldBlur = useCallback(
    async (field: FormField) => {
      setTouched((prev) => ({ ...prev, [field]: true }));

      if (field === 'supplierName') {
        await checkSupplierName(values.supplierName);
      }

      if (field === 'apId') {
        await checkApId(values.apId);
      }

      if (field === 'externalSupplierName' && values.apId.trim()) {
        await checkApId(values.apId);
      }
    },
    [checkApId, checkSupplierName, values.apId, values.supplierName]
  );

  const syncErrors = useMemo(
    () =>
      getSupplierFormSyncErrors({
        values: fromCreateFormValues(values),
        fieldAccess: CREATE_SUPPLIER_FIELD_ACCESS,
        requiredFields,
        t,
      }),
    [requiredFields, t, values]
  );

  const errors = useMemo(() => ({ ...syncErrors, ...asyncErrors }), [asyncErrors, syncErrors]);

  const isValid = useMemo(() => Object.keys(errors).length === 0, [errors]);

  const isDirty = useMemo(
    () => JSON.stringify(values) !== JSON.stringify(initialCreateOfflineSupplierFormValues),
    [values]
  );

  const handleSave = useCallback(async () => {
    setTouched(
      Object.fromEntries(
        (Object.keys(values) as FormField[]).map((field) => [field, true])
      ) as Partial<Record<FormField, boolean>>
    );

    const [nameOk, apIdOk] = await Promise.all([
      checkSupplierName(values.supplierName),
      checkApId(values.apId),
    ]);

    if (!nameOk || !apIdOk || !isValid) {
      return;
    }

    try {
      const offlineSupplierId = await upsertOfflineSupplier(
        mapCreateFormToOfflineUpsertRequest(values)
      );

      if (
        values.transmissionType === 'email' &&
        values.orderEmail.trim() &&
        offlineSupplierId != null
      ) {
        try {
          await sendEmailVerification({ offlineSupplierId });
        } catch {
          // API errors are surfaced by the axios response interceptor.
          return;
        }
      }

      toast.success(t('Supplier created successfully.'));
      resetForm();
      onSuccess?.();
    } catch {
      // API errors are surfaced by the axios response interceptor.
    }
  }, [
    checkApId,
    checkSupplierName,
    isValid,
    onSuccess,
    resetForm,
    sendEmailVerification,
    t,
    upsertOfflineSupplier,
    values,
  ]);

  return {
    values,
    errors,
    touched,
    isDirty,
    isValid,
    isSaving,
    requiredFields,
    setFieldValue,
    handleFieldBlur,
    resetForm,
    handleSave,
  };
};
